import Field, { FieldProps } from './Field'
import {
  Controller,
  ControllerProps,
  ControllerRenderProps,
  FieldValues,
  Path
} from 'react-hook-form'
import React, { cloneElement, useCallback } from 'react'
import moment, { Moment } from 'moment'
import numeral from 'numeral'
import useNormalize from 'core/hooks/useNormalize'

/**
 * @description A field that is tightly integrated with the react-hook-form library. Provides convenience of controlling form fields and validating them.
 */
interface SmartFieldProps<T extends FieldValues> extends FieldProps {
  controllerProps: Omit<ControllerProps<T>, 'render'>
  onChangeOverride?: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    e: any,
    field: ControllerRenderProps<T, Path<T>>
  ) => void
  validateZipCode?: boolean
}

function moneyFormat(nums: string): string {
  let res = ''
  if (nums.length === 0) return '$0.00'
  if (nums.length <= 2) {
    const decimals = nums.length === 2 ? nums : '0' + nums
    res = numeral('0.' + decimals).format('$0,0.00')
  } else {
    const decimals = nums.substring(nums.length - 2)
    const rest = nums.substring(0, nums.length - 2)
    res = numeral(`${rest}.${decimals}`).format('$0,0.00')
  }
  return res
}

function distanceFormat(nums: string): string {
  let res = ''
  if (nums.length === 0) return '0.0'
  if (nums.length <= 1) {
    const decimals = nums.length === 1 ? nums : '0' + nums
    res = numeral('0.' + decimals).format('0,0.0')
  } else {
    const decimals = nums.substring(nums.length - 1)
    const rest = nums.substring(0, nums.length - 1)
    res = numeral(`${rest}.${decimals}`).format('0,0.0')
  }
  return res
}

function decimalCleanValue(value: string): string {
  let cleanValue = value?.replaceAll(/(\$|,|\.)/g, '')
  while (cleanValue.length > 0 && cleanValue.startsWith('0')) {
    cleanValue = cleanValue.substring(1)
  }
  return cleanValue
}

const SmartField = <T extends FieldValues>({
  controllerProps,
  children,
  helpText,
  required,
  invalid,
  onChangeOverride,
  validateZipCode = true,
  ...rest
}: SmartFieldProps<T>) => {
  const { normalizePhone } = useNormalize()
  const isTelType = children.props?.type === 'tel'
  const isMoneyType =
    children.props?.type === 'money' || children.props?.type === 'dollar' // Added dollar type to support BE property value in compliance driver form
  const isDistanceType = children.props?.type === 'distance'
  const isRangeDateType = children.props?.type === 'range-picker'
  const isNewRangeDateType = children.props?.type === 'date-range'
  const isDatePicker = children.props?.type === 'date-picker'
  const isZip = children.props?.name === 'zip_code'
  const isVin = children.props?.name === 'vin'
  const isMobile = children.props?.name === 'mobile'
  const isOdometer = children.props?.name === 'odometer'
  const isYear =
    children.props?.type === 'year' || children.props?.name === 'year'

  const setSelectionToEnd = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const end = e.target?.value?.length
      e.target?.setSelectionRange(end, end)
    },
    []
  )

  const getValue = useCallback(
    (fieldValue: string | string[] | number | undefined | null) => {
      if (isRangeDateType || isNewRangeDateType) {
        return fieldValue?.toString().length
          ? (fieldValue as string[])?.map((v: string) => moment(v))
          : undefined
      } else if (isDatePicker) {
        return fieldValue ? moment(fieldValue) : null
      }
      return fieldValue
    },
    [isDatePicker, isNewRangeDateType, isRangeDateType]
  )

  return (
    <Controller
      {...controllerProps}
      render={({ field, fieldState }) => (
        <Field
          invalid={invalid || !!fieldState.error?.message}
          helpText={
            fieldState.error?.message ? (
              <div className="text-danger text-sm mt-2">
                {fieldState.error?.message}
              </div>
            ) : (
              helpText
            )
          }
          required={required || !!controllerProps.rules?.required}
          {...rest}
        >
          {cloneElement(children, {
            ...children.props,
            ...field,
            value: getValue(field.value),
            onChange: (e: React.ChangeEvent<HTMLInputElement> | T) => {
              // Money type input value is updated on key down
              if (isMoneyType || isDistanceType) return

              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              if (onChangeOverride) return onChangeOverride(e as any, field)

              if (isTelType || isMobile) {
                field.onChange({
                  ...e,
                  target: {
                    ...e.target,
                    value: normalizePhone(e.target.value)
                  }
                })
              } else if (isRangeDateType) {
                const value = e as unknown as [Moment, Moment]
                field.onChange({
                  target: {
                    value: value.map((v) => v.format('MM/DD/YYYY'))
                  }
                })
              } else if (!isNewRangeDateType && children?.props?.format) {
                field.onChange({
                  target: {
                    value: e.target.value
                      ? numeral(e.target.value).format(children.props.format)
                      : ''
                  }
                })
              } else if (isZip && validateZipCode) {
                field.onChange({
                  ...e,
                  target: {
                    ...e.target,
                    value: e.target.value.replace(/\D/g, '').slice(0, 5)
                  }
                })
              } else if (isYear) {
                field.onChange({
                  target: {
                    value: e.target.value.replace(/\D/g, '').slice(0, 4)
                  }
                })
              } else if (isVin) {
                field.onChange({
                  target: {
                    value: e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, '')
                  }
                })
              } else if (isOdometer) {
                field.onChange({
                  target: {
                    value: String(e.target.value)
                      .replace(/\D/g, '')
                      .replace(/,/g, '')
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                })
              } else {
                field.onChange(e)
              }
            },
            onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
              if (isMoneyType || isDistanceType) {
                setTimeout(() => {
                  setSelectionToEnd(e)
                })
              }
            },
            onSelect: (e: React.FocusEvent<HTMLInputElement>) => {
              if (isMoneyType || isDistanceType) {
                setSelectionToEnd(e)
              }
            },
            onPaste(e: React.ClipboardEvent) {
              if (!isMoneyType && !isDistanceType) return
              let text = (e.clipboardData.getData('Text') ?? '').replace(
                /[^0-9.]/g,
                ''
              )
              const [numbers, decimals] = text.split('.')
              if (decimals) {
                const limit = isMoneyType ? 2 : 1
                if (decimals.length < limit) {
                  text = `${numbers}.${decimals}0`
                } else if (decimals.length === limit) {
                  text = `${numbers}.${decimals}`
                } else {
                  const roundedDecimals = Math.round(
                    Number(
                      `${decimals.substring(0, limit)}.${decimals.substring(limit)}`
                    )
                  )
                  text = `${numbers}.${roundedDecimals}`
                }
              } else {
                text = `${numbers}.${isMoneyType ? '00' : '0'}`
              }
              const cleanValue = text.replaceAll('.', '')

              if (isNaN(Number(cleanValue))) return

              field.onChange({
                target: {
                  value: isMoneyType
                    ? moneyFormat(cleanValue)
                    : distanceFormat(cleanValue)
                }
              })
            },
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if ((!isMoneyType && !isDistanceType) || e.code === 'KeyV') return
              const digit = e.code.charAt(e.code.length - 1)
              if (isNaN(parseInt(digit))) {
                e.preventDefault()
                if (e.code === 'Backspace') {
                  if (isMoneyType) {
                    let cleanValue = decimalCleanValue(field.value)
                    cleanValue = cleanValue.substring(0, cleanValue.length - 1)
                    field.onChange({
                      target: {
                        value: moneyFormat(cleanValue)
                      }
                    })
                  }
                  if (isDistanceType) {
                    let cleanValue = decimalCleanValue(
                      field.value?.toString() ?? ''
                    )
                    cleanValue = cleanValue.substring(0, cleanValue.length - 1)
                    field.onChange({
                      target: {
                        value: distanceFormat(cleanValue)
                      }
                    })
                  }
                }
              } else {
                if (isMoneyType) {
                  const cleanValue = decimalCleanValue(field.value)
                  field.onChange({
                    target: {
                      value: moneyFormat(cleanValue + digit)
                    }
                  })
                }
                if (isDistanceType) {
                  const cleanValue = decimalCleanValue(
                    field.value?.toString() ?? ''
                  )
                  field.onChange({
                    target: {
                      value: distanceFormat(cleanValue + digit)
                    }
                  })
                }
              }
            }
          })}
        </Field>
      )}
    />
  )
}

export default SmartField
